// TeamFilterBar — search / region / role / sort controls for the public Team
// Visualization overview (PRODOPSAI-532). Presentational: all state is owned by
// the page and round-tripped through the URL. The region and role selects are
// hidden when there is nothing to filter on.

import type { RoleOption, RegionOption, TeamSortDirection } from '@/components/admin/team-role-tree-helpers';

interface TeamFilterBarProps {
  search: string;
  onSearchChange: (value: string) => void;
  regions: RegionOption[];
  region: string;
  onRegionChange: (value: string) => void;
  roles: RoleOption[];
  role: string;
  onRoleChange: (value: string) => void;
  sort: TeamSortDirection;
  onSortChange: (value: TeamSortDirection) => void;
  hasActiveFilters: boolean;
  onClear: () => void;
}

const SELECT_CLASS =
  'rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground';

export function TeamFilterBar({
  search,
  onSearchChange,
  regions,
  region,
  onRegionChange,
  roles,
  role,
  onRoleChange,
  sort,
  onSortChange,
  hasActiveFilters,
  onClear,
}: Readonly<TeamFilterBarProps>) {
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="team-filter-bar">
      <input
        type="search"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder="Search teams or people…"
        aria-label="Search teams or people"
        className="min-w-56 flex-1 rounded-md border border-border bg-background px-3 py-1.5 text-sm text-foreground"
      />

      {regions.length > 0 && (
        <select
          value={region}
          onChange={(e) => onRegionChange(e.target.value)}
          aria-label="Filter by region"
          className={SELECT_CLASS}
        >
          <option value="">All regions</option>
          {regions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      )}

      {roles.length > 0 && (
        <select
          value={role}
          onChange={(e) => onRoleChange(e.target.value)}
          aria-label="Filter by role"
          className={SELECT_CLASS}
        >
          <option value="">All roles</option>
          {roles.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      )}

      <select
        value={sort}
        onChange={(e) => onSortChange(e.target.value === 'desc' ? 'desc' : 'asc')}
        aria-label="Sort teams"
        className={SELECT_CLASS}
      >
        <option value="asc">Sort: A–Z</option>
        <option value="desc">Sort: Z–A</option>
      </select>

      {hasActiveFilters && (
        <button
          type="button"
          onClick={onClear}
          className="text-sm text-primary hover:underline"
        >
          Clear filters
        </button>
      )}
    </div>
  );
}
